import {
  BarChart3Icon,
  BellIcon,
  BookOpenIcon,
  BotIcon,
  CalendarDaysIcon,
  ContactIcon,
  CreditCardIcon,
  FileSignatureIcon,
  FileTextIcon,
  KeyRoundIcon,
  LayersIcon,
  ListChecksIcon,
  RefreshCwIcon,
  MessageSquareIcon,
  PackageIcon,
  PhoneCallIcon,
  PhoneIcon,
  SettingsIcon,
  ShareIcon,
  SparklesIcon,
  UserPlusIcon,
  UsersIcon,
  WorkflowIcon,
  ZapIcon,
} from 'lucide-react'
import type { Section } from '../types/onboarding'

export const sections: Section[] = [
  {
    id: 'logging-in',
    number: 1,
    navLabel: 'Logging in',
    title: 'Logging in',
    purpose: 'Get into your account for the first time and make it yours.',
    icon: KeyRoundIcon,
    kind: 'steps',
    mediaLabel: '▶ Short walkthrough — coming soon',
    steps: [
      'Open your welcome email and click the login link, or go straight there: [Login Here](https://crm.viridiaanalytics.com/).',
      'Enter the email address the welcome email was sent to.',
      'Enter the temporary password from that same email.',
      'When prompted, set your own password — something you\'ll remember.',
      'Bookmark the login page on your phone and computer so it\'s always one tap away.',
    ],
    note: 'Can\'t find the welcome email? Check your spam folder, then reach out and we\'ll resend it.',
  },
  {
    id: 'creating-users',
    number: 2,
    navLabel: 'Add your team',
    title: 'Creating users (add your team)',
    purpose: 'Give your office staff and techs their own logins.',
    icon: UsersIcon,
    kind: 'steps',
    mediaLabel: '▶ Short walkthrough — coming soon',
    steps: [
      'Go to Settings → My Staff.',
      'Click Add Employee.',
      'Fill in their first name, last name, email and phone number.',
      'Choose their role: User for day-to-day team members, Admin for people who should manage settings and billing.',
      'Click Save — they\'ll get an invite email with their own login details.',
    ],
    note: 'Not sure who needs Admin? Start everyone as a User — you can change it any time.',
  },
  {
    id: 'crm-tour',
    number: 3,
    navLabel: 'Tour of your CRM',
    title: 'A tour of your CRM',
    purpose: 'A quick lap around the main areas so nothing feels unfamiliar.',
    icon: BarChart3Icon,
    kind: 'tour',
    mediaLabel: '▶ Guided tour video — coming soon',
    steps: [
      'Use the left-hand menu to move between areas — it stays with you everywhere.',
      'Click an area to open it; the menu highlights where you are.',
      'Have a click around — you can\'t break anything.',
    ],
    areas: [
      {
        name: 'Dashboard',
        icon: BarChart3Icon,
        description: 'Your at-a-glance numbers — new leads, calls and revenue.',
      },
      {
        name: 'Conversations',
        icon: MessageSquareIcon,
        description:
          'Every call, text and email in one inbox. This is where "never miss a lead" happens.',
        highlight: true,
      },
      {
        name: 'Contacts',
        icon: ContactIcon,
        description: 'Your full list of customers and leads, always searchable.',
      },
      {
        name: 'Opportunities / Pipelines',
        icon: LayersIcon,
        description:
          'The stages a lead moves through — drag a card along as the job progresses. The default pipeline works great; custom ones are optional.',
      },
      {
        name: 'Calendars',
        icon: CalendarDaysIcon,
        description: 'Your bookings and the hours you\'re available for work.',
      },
      {
        name: 'Invoices & Estimates',
        icon: CreditCardIcon,
        description: 'Send a quote, turn it into an invoice, and get paid.',
      },
    ],
  },
  {
    id: 'creating-a-contact',
    number: 4,
    navLabel: 'Create a contact',
    title: 'Creating a contact',
    purpose: 'Add a customer by hand — handy for jobs that come in word-of-mouth.',
    icon: UserPlusIcon,
    kind: 'steps',
    mediaLabel: '▶ Short walkthrough — coming soon',
    steps: [
      'Go to Contacts.',
      'Click Add Contact.',
      'Enter their name, phone number and email address.',
      'Click Save — they\'re now in your system and searchable.',
    ],
    note: 'Already have a customer list in a spreadsheet? You can import it all at once with a CSV file — send it our way and we\'ll help you get it in.',
  },
  {
    id: 'calendar-sync',
    number: 5,
    navLabel: 'Calendar sync',
    title: 'Calendar sync (Google or Outlook)',
    purpose: 'Keep your bookings and your personal calendar in step, both ways.',
    icon: RefreshCwIcon,
    kind: 'steps',
    mediaLabel: '▶ Short walkthrough — coming soon',
    steps: [
      'Open your Profile from the [profile menu label] in the top corner.',
      'Find the calendar section and choose Connect Google or Connect Outlook.',
      'Sign in and allow access so the sync runs both ways — new bookings land on your calendar, and your personal events block out your availability.',
      'Optional: set your available hours so you only get booked when you\'re actually working.',
    ],
    note: 'Your email connection lives on the same screen — connecting it means replies land in Conversations too.',
  },
  {
    id: 'social-accounts',
    number: 6,
    navLabel: 'Social accounts',
    title: 'Connect your social accounts',
    purpose: 'Post to all your channels from one place — and catch messages there too.',
    icon: ShareIcon,
    kind: 'steps',
    mediaLabel: '▶ Short walkthrough — coming soon',
    steps: [
      'Go to Marketing → Social Planner (or Settings → Integrations).',
      'Click Add Account / Connect Account.',
      'Pick a platform: Facebook, Instagram, Google Business Profile, LinkedIn or TikTok.',
      'Sign in to that account and approve the connection.',
      'Repeat for each channel you use.',
    ],
    note: 'Only connect what you actually post to — you can always add more later.',
  },
  {
    id: 'ai-knowledge-base',
    number: 7,
    navLabel: 'AI knowledge base',
    title: 'Your AI receptionist — knowledge base',
    purpose: 'What your AI receptionist knows about your business, so it answers like you would.',
    icon: BookOpenIcon,
    kind: 'white-glove',
    mediaLabel: '▶ Short walkthrough — coming soon',
    togetherPoints: [
      'Your opening hours and after-hours handling',
      'The services you offer — and the ones you don\'t',
      'Rough pricing or call-out fees you\'re happy to share',
      'Your service area',
      'How you\'d like callers greeted',
    ],
    note: 'Nothing to set up on your own. We\'ll walk through these with you on a call and write it all up for you. Voice AI is set up on the desktop app.',
  },
  {
    id: 'ai-agent-number',
    number: 8,
    navLabel: 'AI agent & number',
    title: 'Your AI receptionist — agent & phone number',
    purpose: 'The dedicated number your AI answers on, 24/7.',
    icon: PhoneCallIcon,
    kind: 'white-glove',
    mediaLabel: '▶ Short walkthrough — coming soon',
    togetherPoints: [
      'We help you choose a dedicated phone number',
      'We connect your AI agent to that number',
      'We decide together how calls are forwarded from your existing line',
      'We test it side by side before it goes live',
    ],
    note: 'Once it\'s live, every call is answered — day, night, weekends and while you\'re under a sink. Voice AI is set up on the desktop app.',
  },
  {
    id: 'explore-more',
    number: 9,
    navLabel: 'Explore everything else',
    title: 'Explore everything else',
    purpose: 'A few more tools waiting for you whenever you\'re ready.',
    icon: SparklesIcon,
    kind: 'explore',
    mediaLabel: '▶ Feature overview — coming soon',
    items: [
      { name: 'Dialer', icon: PhoneIcon, description: 'Call customers straight from your CRM.' },
      { name: 'Tasks', icon: ListChecksIcon, description: 'Simple to-dos so follow-ups don\'t slip.' },
      { name: 'Ask AI', icon: SparklesIcon, description: 'Ask questions about your business in plain English.' },
      { name: 'Notifications', icon: BellIcon, description: 'Choose exactly what pings you and where.' },
      { name: 'POS', icon: CreditCardIcon, description: 'Take payment on the spot, at the job.' },
      { name: 'Products', icon: PackageIcon, description: 'Save your services and prices once, reuse forever.' },
      { name: 'Documents & Contracts', icon: FileSignatureIcon, description: 'Send agreements and collect signatures.' },
      { name: 'Automations', icon: WorkflowIcon, description: 'Follow-ups that send themselves.' },
      { name: 'AI Agents', icon: BotIcon, description: 'More helpers beyond the receptionist.' },
      { name: 'Settings', icon: SettingsIcon, description: 'Your business details, team and billing.' },
    ],
  },
]

export const extras = {
  quickWins: [
    { icon: ZapIcon, label: 'Most owners finish steps 1–6 in about 20 minutes.' },
    { icon: FileTextIcon, label: 'No need to do it in one sitting — your progress is saved here.' },
  ],
}
